/**
 * IndexedDB persistence layer for RecCache
 * Provides fast startup by caching individuals, edges, and derived data
 *
 * User-specific caching: Each authenticated user gets their own IndexedDB database
 * to ensure complete data isolation and privacy.
 */

import {
  ObjectProperty,
  DataProperty,
  Iri,
  Literal,
  LanguageString,
  JsonLiteral,
} from './rec-cache-types'
import { CACHE_FORMAT_VERSION } from '@/rec-cache/rec-cache-format'

const DB_VERSION = 1

// The client's change-detection state (for ADT: twin etags and relationship
// hashes) is kept in the metadata store under its own key, beside the cache
// it describes. The metadata store already takes arbitrary keys, so this needs
// no schema change and no DB_VERSION bump — a bump would leave any tab still
// open on the previous release blocking the upgrade.
const CHANGE_DETECTION_KEY = 'changeDetection'
// Shared with the etagMap key in adt-client.js so the two halves of the cache
// can never disagree — see rec-cache-format.js. (Historically a local
// constant; v5 was "derive edges/aggregates/geoCoords from individuals
// instead of persisting them".)
const CACHE_VERSION = CACHE_FORMAT_VERSION
const INDIVIDUALS_STORE = 'individuals'
const METADATA_STORE = 'metadata'
const EDGES_STORE = 'edges'
const AGGREGATES_STORE = 'aggregates'
const GEO_COORDS_STORE = 'geoCoordinates'

class RecCacheIDB {
  /**
   * @param {string|null} userId - User identifier for database isolation
   *   - For MSAL: Use account.homeAccountId (format: "localAccountId.tenantId")
   *   - For other auth providers: Use a stable, unique identifier (not email/username)
   *   - null = Skip IDB initialization (anonymous/unauthenticated users)
   */
  constructor(userId = null) {
    this.userId = userId
    this.db = null
    this.isReady = false
  }

  /**
   * Generate user-specific database name
   * @returns {string} Database name like "RecCacheDB_userId"
   * @private
   */
  getDbName() {
    if (!this.userId) {
      // Should never be called for anonymous users, but provide fallback
      return 'RecCacheDB_anonymous'
    }

    // Sanitize userId to create valid database name
    // Replace any non-alphanumeric characters (except hyphens) with underscores
    const sanitized = this.userId.replace(/[^a-zA-Z0-9-]/g, '_')
    return `RecCacheDB_${sanitized}`
  }

  /**
   * Initialize IndexedDB connection and create object stores
   * Opens a user-specific database to isolate cached data between users
   * Handles blocked connections and corrupt databases with automatic recovery
   * @returns {Promise<void>}
   */
  async init() {
    const dbName = this.getDbName()

    try {
      await this._openDatabase(dbName)
    } catch (error) {
      // If open failed (corrupt DB, blocked too long, etc.), try to recover
      // by deleting the database and opening fresh
      console.warn(
        `RecCacheIDB: Database open failed, attempting recovery by deleting database (${dbName})`,
        error
      )
      try {
        await this._deleteDatabase(dbName)
        await this._openDatabase(dbName)
        console.log(
          `RecCacheIDB: Recovery successful - opened fresh database (${dbName})`
        )
      } catch (retryError) {
        console.error(
          `RecCacheIDB: Recovery failed, IndexedDB caching disabled (${dbName})`,
          retryError
        )
        throw retryError
      }
    }
  }

  /**
   * Open (or create) the IndexedDB database
   * @param {string} dbName
   * @returns {Promise<void>}
   * @private
   */
  _openDatabase(dbName) {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(dbName, DB_VERSION)

      // If another connection (e.g. another tab or a stale pre-reload connection)
      // is holding the database, the open is blocked. Wait up to 3 seconds then
      // reject so the caller can recover by deleting the DB.
      let blockedTimer = null
      request.onblocked = () => {
        console.warn(
          `RecCacheIDB: Database open blocked by another connection (${dbName})`
        )
        blockedTimer = setTimeout(() => {
          reject(new Error(`Database open blocked for too long (${dbName})`))
        }, 3000)
      }

      request.onerror = () => {
        clearTimeout(blockedTimer)
        console.error('RecCacheIDB: Failed to open database', request.error)
        reject(request.error)
      }

      request.onsuccess = () => {
        clearTimeout(blockedTimer)
        this.db = request.result
        this.isReady = true

        // Handle versionchange events from other tabs/contexts trying to
        // upgrade or delete this database. Close gracefully so they aren't blocked.
        this.db.onversionchange = () => {
          console.log(
            `RecCacheIDB: Database version change requested, closing connection (${dbName})`
          )
          this.db.close()
          this.db = null
          this.isReady = false
        }

        // Handle unexpected connection close
        this.db.onclose = () => {
          console.warn(
            `RecCacheIDB: Database connection closed unexpectedly (${dbName})`
          )
          this.db = null
          this.isReady = false
        }

        console.log(`RecCacheIDB: Database opened successfully (${dbName})`)
        resolve()
      }

      request.onupgradeneeded = (event) => {
        const db = event.target.result

        // Create individuals store with id as keyPath
        if (!db.objectStoreNames.contains(INDIVIDUALS_STORE)) {
          db.createObjectStore(INDIVIDUALS_STORE, { keyPath: 'id' })
          console.log('RecCacheIDB: Created individuals store')
        }

        // Create metadata store (key-value pairs)
        if (!db.objectStoreNames.contains(METADATA_STORE)) {
          db.createObjectStore(METADATA_STORE)
          console.log('RecCacheIDB: Created metadata store')
        }

        // Create edges store with auto-increment key
        if (!db.objectStoreNames.contains(EDGES_STORE)) {
          db.createObjectStore(EDGES_STORE, { autoIncrement: true })
          console.log('RecCacheIDB: Created edges store')
        }

        // Create aggregates store (key-value pairs)
        if (!db.objectStoreNames.contains(AGGREGATES_STORE)) {
          db.createObjectStore(AGGREGATES_STORE)
          console.log('RecCacheIDB: Created aggregates store')
        }

        // Create geoCoordinates store (key-value pairs)
        if (!db.objectStoreNames.contains(GEO_COORDS_STORE)) {
          db.createObjectStore(GEO_COORDS_STORE)
          console.log('RecCacheIDB: Created geoCoordinates store')
        }
      }
    })
  }

  /**
   * Delete the IndexedDB database entirely (for recovery from corruption)
   * @param {string} dbName
   * @returns {Promise<void>}
   * @private
   */
  _deleteDatabase(dbName) {
    return new Promise((resolve, reject) => {
      const request = indexedDB.deleteDatabase(dbName)
      request.onsuccess = () => {
        console.log(`RecCacheIDB: Deleted database (${dbName})`)
        resolve()
      }
      request.onerror = () => {
        console.error(
          `RecCacheIDB: Failed to delete database (${dbName})`,
          request.error
        )
        reject(request.error)
      }
      request.onblocked = () => {
        console.warn(
          `RecCacheIDB: Database deletion blocked (${dbName}), resolving anyway`
        )
        // Resolve anyway - the deletion will complete when connections close
        resolve()
      }
    })
  }

  /**
   * Save individuals to IndexedDB
   * @param {Array} individuals - Array of RecIndividual objects
   * @returns {Promise<void>}
   */
  async saveIndividuals(individuals) {
    if (!this.isReady) {
      console.warn('RecCacheIDB: Database not ready, skipping save')
      return
    }

    const transaction = this.db.transaction([INDIVIDUALS_STORE], 'readwrite')
    const store = transaction.objectStore(INDIVIDUALS_STORE)

    return new Promise((resolve, reject) => {
      // Clear all existing individuals first to remove stale entries
      // This prevents old component IDs from previous sessions persisting
      const clearRequest = store.clear()

      clearRequest.onsuccess = () => {
        // Use a counter to track progress
        let processed = 0
        const total = individuals.length

        individuals.forEach((individual) => {
          // Convert RecIndividual to plain object for storage
          const serialized = this._serializeIndividual(individual)
          const request = store.put(serialized)

          request.onsuccess = () => {
            processed++
            if (processed === total) {
              console.log(
                `RecCacheIDB: Saved ${total} individuals to IndexedDB`
              )
            }
          }

          request.onerror = () => {
            console.error('RecCacheIDB: Error saving individual', request.error)
          }
        })
      }

      clearRequest.onerror = () => {
        console.error('RecCacheIDB: Error clearing store', clearRequest.error)
        reject(clearRequest.error)
      }

      transaction.oncomplete = () => resolve()
      transaction.onerror = () => reject(transaction.error)
    })
  }

  /**
   * Load all individuals from IndexedDB
   * @returns {Promise<Array>}
   */
  async loadIndividuals() {
    if (!this.isReady) {
      console.warn('RecCacheIDB: Database not ready, returning empty array')
      return []
    }

    const transaction = this.db.transaction([INDIVIDUALS_STORE], 'readonly')
    const store = transaction.objectStore(INDIVIDUALS_STORE)
    const request = store.getAll()

    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        const individuals = request.result || []
        console.log(
          `RecCacheIDB: Loaded ${individuals.length} individuals from IndexedDB`
        )
        resolve(individuals)
      }

      request.onerror = () => {
        console.error('RecCacheIDB: Error loading individuals', request.error)
        reject(request.error)
      }
    })
  }

  // NOTE: Edges, aggregates, and geoCoordinates are NOT persisted to IDB.
  // They are derived data reconstructed from individuals on hydration.
  // This eliminates an entire class of cache consistency bugs.

  /**
   * Update only the lastSyncTime in metadata (without rewriting all data)
   * Called on every sync complete to track when API was last checked
   * @param {number} timestamp
   * @returns {Promise<void>}
   */
  async updateLastSyncTime(timestamp) {
    if (!this.isReady) return

    const transaction = this.db.transaction([METADATA_STORE], 'readwrite')
    const store = transaction.objectStore(METADATA_STORE)

    return new Promise((resolve, reject) => {
      const getRequest = store.get('cache')
      getRequest.onsuccess = () => {
        const metadata = getRequest.result
        if (metadata) {
          metadata.lastSyncTime = timestamp
          store.put(metadata, 'cache')
        }
      }

      transaction.oncomplete = () => resolve()
      transaction.onerror = () => reject(transaction.error)
    })
  }

  /**
   * Load metadata
   * @returns {Promise<Object|null>} Returns null if version mismatch or no data
   */
  async loadMetadata() {
    if (!this.isReady) return null

    const transaction = this.db.transaction([METADATA_STORE], 'readonly')
    const store = transaction.objectStore(METADATA_STORE)
    const request = store.get('cache')

    const metadata = await new Promise((resolve, reject) => {
      request.onsuccess = () => {
        resolve(request.result || null)
      }
      request.onerror = () => reject(request.error)
    })

    if (!metadata) {
      console.log('RecCacheIDB: No metadata found')
      return null
    }

    // Check cache version - clear stale data AFTER the read transaction completes
    if (metadata.cacheVersion !== CACHE_VERSION) {
      console.warn(
        `RecCacheIDB: Cache version mismatch (found ${metadata.cacheVersion}, expected ${CACHE_VERSION}). Clearing old cache...`
      )
      await this.clear()
      return null
    }

    console.log('RecCacheIDB: Loaded metadata', metadata)
    return metadata
  }

  /**
   * Load the client's change-detection state saved by saveAll().
   * Opaque to the cache: whatever the client handed over comes back as is.
   * @returns {Promise<any|null>} null when none has been saved yet
   */
  async loadChangeDetection() {
    if (!this.isReady) return null

    const transaction = this.db.transaction([METADATA_STORE], 'readonly')
    const store = transaction.objectStore(METADATA_STORE)
    const request = store.get(CHANGE_DETECTION_KEY)

    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result || null)
      request.onerror = () => reject(request.error)
    })
  }

  /**
   * Save all cache data atomically in a single IndexedDB transaction.
   * Only individuals and metadata are persisted — derived data (edges,
   * aggregates, geoCoordinates) is reconstructed from individuals on load.
   * @param {Object} data - { individuals, metadata, changeDetection }
   *   changeDetection is the client's opaque state; omitted by clients that
   *   keep none.
   * @returns {Promise<void>}
   */
  async saveAll({ individuals, metadata, changeDetection }) {
    if (!this.isReady) {
      console.warn('RecCacheIDB: Database not ready, skipping save')
      return
    }

    // Single transaction across stores ensures atomicity:
    // either all stores are updated or none are (on crash/reload)
    const transaction = this.db.transaction(
      [INDIVIDUALS_STORE, METADATA_STORE],
      'readwrite'
    )

    const indStore = transaction.objectStore(INDIVIDUALS_STORE)
    const metaStore = transaction.objectStore(METADATA_STORE)

    return new Promise((resolve, reject) => {
      // Clear individuals store first
      indStore.clear()

      // Write individuals
      individuals.forEach((individual) => {
        indStore.put(this._serializeIndividual(individual))
      })

      // Write metadata (with cache version) in the same transaction
      const versionedMetadata = { ...metadata, cacheVersion: CACHE_VERSION }
      metaStore.put(versionedMetadata, 'cache')

      // The client's change-detection state goes in the same transaction, so
      // it can never describe a different set of individuals than the one
      // stored beside it. It used to be written to localStorage on its own
      // schedule; the two could disagree, and localStorage's ~5MB cap made the
      // write fail outright once the graph grew past ~31k twins.
      if (changeDetection !== undefined) {
        metaStore.put(changeDetection, CHANGE_DETECTION_KEY)
      }

      transaction.oncomplete = () => {
        console.log(
          `RecCacheIDB: Atomic save complete (${individuals.length} individuals)`
        )
        resolve()
      }

      transaction.onerror = () => {
        console.error('RecCacheIDB: Atomic save failed', transaction.error)
        reject(transaction.error)
      }
    })
  }

  /**
   * Clear all cached data
   * @returns {Promise<void>}
   */
  async clear() {
    if (!this.isReady) return

    const transaction = this.db.transaction(
      [
        INDIVIDUALS_STORE,
        METADATA_STORE,
        EDGES_STORE,
        AGGREGATES_STORE,
        GEO_COORDS_STORE,
      ],
      'readwrite'
    )

    const stores = [
      transaction.objectStore(INDIVIDUALS_STORE),
      transaction.objectStore(METADATA_STORE),
      transaction.objectStore(EDGES_STORE),
      transaction.objectStore(AGGREGATES_STORE),
      transaction.objectStore(GEO_COORDS_STORE),
    ]

    return new Promise((resolve, reject) => {
      stores.forEach((store) => store.clear())

      transaction.oncomplete = () => {
        console.log('RecCacheIDB: Cleared all cached data')
        resolve()
      }
      transaction.onerror = () => reject(transaction.error)
    })
  }

  /**
   * Verify a write by reading back the count and a sample individual from IDB
   * Used to confirm that saveAll actually persisted data
   * @param {number} expectedCount - The number of individuals we expected to write
   * @param {string|null} sampleId - Optional individual ID to spot-check
   * @returns {Promise<{ok: boolean, count: number, sampleProps: number|null}>}
   */
  async verifyWrite(expectedCount, sampleId = null) {
    if (!this.isReady) {
      return { ok: false, count: 0, sampleProps: null, reason: 'not ready' }
    }

    const transaction = this.db.transaction(
      [INDIVIDUALS_STORE, METADATA_STORE],
      'readonly'
    )
    const indStore = transaction.objectStore(INDIVIDUALS_STORE)
    const metaStore = transaction.objectStore(METADATA_STORE)

    const countRequest = indStore.count()
    const metaRequest = metaStore.get('cache')
    const sampleRequest = sampleId ? indStore.get(sampleId) : null

    return new Promise((resolve) => {
      transaction.oncomplete = () => {
        const count = countRequest.result
        const meta = metaRequest.result
        const sample = sampleRequest?.result || null

        const result = {
          ok: count === expectedCount,
          count,
          expectedCount,
          metaSize: meta?.size,
          sampleId: sampleId,
          sampleProps: sample?.properties?.length ?? null,
          samplePropNames:
            sample?.properties?.map(
              (p) => p.name?.localName || p.name?.value
            ) ?? null,
          sampleComponentCount: sample?.components?.length ?? null,
        }

        if (!result.ok) {
          console.error(
            'IDB-VERIFY: WRITE VERIFICATION FAILED!',
            `Expected ${expectedCount} individuals, found ${count}`,
            result
          )
        } else {
          console.log(
            `IDB-VERIFY: Write verified OK (${count} individuals)`,
            sampleId ? `Sample ${sampleId}: ${result.sampleProps} props` : ''
          )
        }

        resolve(result)
      }

      transaction.onerror = () => {
        resolve({
          ok: false,
          count: 0,
          sampleProps: null,
          reason: 'transaction error',
        })
      }
    })
  }

  /**
   * Close the database connection
   * Call this when user logs out or switches users
   * @returns {void}
   */
  close() {
    if (this.db) {
      this.db.close()
      console.log(
        `RecCacheIDB: Closed database connection (${this.getDbName()})`
      )
      this.db = null
      this.isReady = false
    }
  }

  /**
   * Delete the entire database for this user (for recovery from corruption)
   * @returns {Promise<void>}
   */
  async deleteDatabase() {
    this.close()
    return this._deleteDatabase(this.getDbName())
  }

  /**
   * Serialize RecIndividual to plain object
   * @private
   */
  _serializeIndividual(individual) {
    // Convert complex objects to plain JSON-serializable format
    return {
      id: individual.id.value, // Store just the value as the key
      idObject: this._serializeIri(individual.id),
      type: this._serializeIri(individual.type),
      types: individual.types.map((t) => this._serializeIri(t)),
      properties: individual.properties.map((p) => this._serializeProperty(p)),
      components: individual.components.map((c) => this._serializeComponent(c)),
      meta: individual.meta
        ? {
            geoCoords: individual.meta.geoCoords
              ? { list: individual.meta.geoCoords.list || [] }
              : null,
          }
        : {},
    }
  }

  /**
   * Serialize IRI object
   * @private
   */
  _serializeIri(iri) {
    if (!iri) return null
    return {
      _type: 'Iri',
      value: iri.value,
    }
  }

  /**
   * Serialize Property object
   * Uses instanceof checks instead of constructor.name to avoid minification issues
   * @private
   */
  _serializeProperty(property) {
    let _type = null
    if (property instanceof ObjectProperty) {
      _type = 'ObjectProperty'
    } else if (property instanceof DataProperty) {
      _type = 'DataProperty'
    }

    return {
      _type,
      name: this._serializeIri(property.name),
      values: property.values.map((v) => this._serializeValue(v)),
    }
  }

  /**
   * Serialize Value (Iri, Literal, LanguageString, JsonLiteral)
   * Uses instanceof checks instead of constructor.name to avoid minification issues
   * @private
   */
  _serializeValue(value) {
    if (!value) return null

    if (value instanceof Iri) {
      return this._serializeIri(value)
    }

    // Determine type using instanceof
    let _type = 'Literal'
    if (value instanceof JsonLiteral) {
      _type = 'JsonLiteral'
    } else if (value instanceof LanguageString) {
      _type = 'LanguageString'
    } else if (value instanceof Literal) {
      _type = 'Literal'
    }

    // Literal.datatype is a string (IRI value), not an Iri object.
    // Serialize it directly as a string rather than calling _serializeIri.
    return {
      _type,
      value: value.value,
      language: value.language || null,
      datatype: value.datatype || null,
    }
  }

  /**
   * Serialize Component
   * @private
   */
  _serializeComponent(component) {
    return {
      id: this._serializeIri(component.id),
      types: component.types?.map((t) => this._serializeIri(t)) || [],
      properties:
        component.properties?.map((p) => this._serializeProperty(p)) || [],
      meta: component.meta
        ? {
            geoCoords: component.meta.geoCoords
              ? { list: component.meta.geoCoords.list || [] }
              : null,
          }
        : {},
    }
  }

  /**
   * Deserialize methods - reconstruct objects with proper classes
   * These are provided to RecCache which has access to the proper class constructors
   */

  /**
   * Get deserialized individuals (plain objects that RecCache will reconstruct)
   * @returns {Promise<Array>}
   */
  async getDeserializedIndividuals() {
    return await this.loadIndividuals()
  }

  /**
   * Get deserialized edges (plain objects)
   * @returns {Promise<Array>}
   */
  async getDeserializedEdges() {
    return await this.loadEdges()
  }

  /**
   * Get deserialized aggregates (Map with plain objects)
   * @returns {Promise<Map>}
   */
  async getDeserializedAggregates() {
    return await this.loadAggregates()
  }

  /**
   * Get deserialized geoCoordinates (Map with plain objects)
   * @returns {Promise<Map>}
   */
  async getDeserializedGeoCoordinates() {
    return await this.loadGeoCoordinates()
  }
}

export { RecCacheIDB }
